<script setup lang="ts">
import { computed, shallowRef, watch } from 'vue'
import type { EventMapExportSnapshot } from './event-map-export.types'
import EventMapRenderer from '~/shared-kernel/presentation/event-map/EventMapRenderer.vue'
import type { EventMapDocument, EventMapUnitDetails } from '~/shared-kernel/presentation/event-map/event-map.types'
import {
  useInventoryAvailabilityRealtime,
  type InventoryRealtimeContext
} from '~/shared-kernel/presentation/composables/useInventoryAvailabilityRealtime'
import type { InventoryMapPatch } from '~/shared-kernel/domain/inventory-map-patch'

const props = withDefaults(defineProps<{
  loadExportSnapshot?: () => Promise<EventMapExportSnapshot>
  document?: EventMapDocument | null
  selectedEventId?: string
  currency?: string
  loadDocument?: (eventId: string, currency: string) => Promise<EventMapDocument | null>
  realtimeContext: InventoryRealtimeContext
  focusedLocalityId: string | null
  inspectedUnitId: string | null
  ownedUnitIds?: string[]
  pendingUnitIds?: string[]
  ownedLocalityIds?: string[]
  pendingLocalityIds?: string[]
  unitDetails?: Record<string, EventMapUnitDetails>
  localityCaptions?: Record<string, string>
  selectable?: boolean
  activatable?: boolean
  showActivationTooltip?: boolean
  showBackToMap?: boolean
  showReferenceSections?: boolean
  refreshAvailability?: () => void | Promise<void>
  // Content version of the map shown, so the server can skip the join resync.
  knownVersion?: string | null | undefined
  // Applies a realtime map patch; resolve false to fall back to a full reload.
  applyPatch?: ((patch: InventoryMapPatch) => boolean | Promise<boolean>) | undefined
}>(), {
  ownedUnitIds: () => [],
  pendingUnitIds: () => [],
  ownedLocalityIds: () => [],
  pendingLocalityIds: () => [],
  unitDetails: () => ({}),
  selectable: true,
  showBackToMap: true,
  activatable: false,
  showActivationTooltip: false,
  showReferenceSections: true
})

const emit = defineEmits<{
  focusLocality: [localityId: string | null]
  inspectUnit: [unitId: string | null]
  selectUnit: [unitId: string]
  activateUnit: [unitId: string]
  selectLocality: [localityId: string]
  inventoryChanged: []
  inventoryResync: []
  loadError: [error: unknown]
}>()

const loadedDocument = shallowRef<EventMapDocument | null>(null)
const loading = shallowRef(false)
const loadError = shallowRef<unknown>(null)
let loadSequence = 0
let foregroundSequence = 0

const renderedDocument = computed(() => loadedDocument.value ?? props.document ?? null)

async function loadSelectedDocument(background = false) {
  const eventId = props.selectedEventId
  const currency = props.currency
  const loadDocument = props.loadDocument
  const sequence = ++loadSequence
  const foreground = background ? null : ++foregroundSequence
  if (!background) {
    loadedDocument.value = null
    loadError.value = null
  }
  if (!eventId || !currency || !loadDocument) {
    if (foreground === foregroundSequence) loading.value = false
    return
  }

  if (!background) loading.value = true
  try {
    const document = await loadDocument(eventId, currency)
    if (sequence === loadSequence) loadedDocument.value = document
  } catch (error) {
    if (sequence !== loadSequence) return
    if (!background) {
      loadError.value = error
      emit('loadError', error)
    }
  } finally {
    // Background invalidations are allowed to supersede the response, but they
    // must never steal ownership of the foreground loading indicator.
    if (foreground !== null && foreground === foregroundSequence) loading.value = false
  }
}

watch(
  [
    () => props.selectedEventId,
    () => props.currency,
    () => props.loadDocument
  ],
  () => loadSelectedDocument(),
  { immediate: true }
)

async function refreshAvailability(kind: 'changed' | 'resync') {
  await Promise.all([
    props.refreshAvailability?.(),
    loadSelectedDocument(true)
  ])
  if (kind === 'changed') emit('inventoryChanged')
  else emit('inventoryResync')
}

useInventoryAvailabilityRealtime({
  context: () => props.realtimeContext,
  knownVersion: () => props.knownVersion,
  onChanged: () => refreshAvailability('changed'),
  onResync: () => refreshAvailability('resync'),
  onPatch: patch => props.applyPatch?.(patch) ?? false
})
</script>

<template>
  <div v-if="loading" class="realtime-event-map-renderer__loading" role="status">
    <TqSkeleton v-for="item in 3" :key="item" />
  </div>
  <TqEmptyState
    v-else-if="loadError"
    :title="$t('eventMap.loadError')"
    :show-mark="false"
  />
  <TqEmptyState v-else-if="!renderedDocument" :title="$t('eventMap.unavailable')" />
  <EventMapRenderer
    v-else
    :document="renderedDocument"
    v-bind="{ ...(loadExportSnapshot ? { loadExportSnapshot } : {}), ...(localityCaptions ? { localityCaptions } : {}) }"
    :focused-locality-id="focusedLocalityId"
    :inspected-unit-id="inspectedUnitId"
    :owned-unit-ids="ownedUnitIds"
    :pending-unit-ids="pendingUnitIds"
    :owned-locality-ids="ownedLocalityIds"
    :pending-locality-ids="pendingLocalityIds"
    :unit-details="unitDetails"
    :selectable="selectable"
    :activatable="activatable"
    :show-activation-tooltip="showActivationTooltip"
    :show-back-to-map="showBackToMap"
    :show-reference-sections="showReferenceSections"
    @focus-locality="emit('focusLocality', $event)"
    @inspect-unit="emit('inspectUnit', $event)"
    @select-unit="emit('selectUnit', $event)"
    @activate-unit="emit('activateUnit', $event)"
    @select-locality="emit('selectLocality', $event)"
  />
</template>

<style scoped>
.realtime-event-map-renderer__loading { display: grid; gap: .6rem; min-height: 15rem; }
</style>
